import { useCallback, useEffect, useRef } from 'react';
import type { UserSession } from '../authAtom';
import decodeAccessTokenExp from '../utils/jwt';

const REFRESH_LEAD_SECONDS = 300 as const;

const useProactiveRefresh = (session: UserSession, trigger: (reason: string) => Promise<boolean>) => {
  const timerRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    clear();
    if (!session) return;
    const now = Math.floor(Date.now() / 1000);
    const exp = decodeAccessTokenExp(session.accessToken) ?? 0;
    const target = exp - REFRESH_LEAD_SECONDS;
    const delayMs = (target - now) * 1000;
    if (delayMs <= 0) {
      void trigger('proactive-expired');
      return;
    }
    timerRef.current = window.setTimeout(() => {
      void trigger('proactive-scheduled');
    }, delayMs);
  }, [session, trigger, clear]);

  useEffect(() => {
    schedule();
    return clear;
  }, [schedule, clear]);
};

export default useProactiveRefresh;
